import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

import { loadClientPageBySlug } from "@/lib/client-api";
import { loadPublicPage, voteOnPage } from "@/lib/pages-api";

export const Route = createFileRoute("/$tenantSlug/pages/$pageSlug")({
  component: PagePage,
});

type Loaded =
  | { source: "shared"; title: string; body_html: string | null; updated_at: string | null }
  | Awaited<ReturnType<typeof loadPublicPage>> & { source: "public" };

function PagePage() {
  const { tenantSlug, pageSlug } = Route.useParams();
  const { data, isLoading, isError, error } = useQuery<Loaded>({
    queryKey: ["portal-page", tenantSlug, pageSlug] as const,
    queryFn: async () => {
      const shared = await loadClientPageBySlug(tenantSlug, pageSlug);
      if (shared) {
        const payload = (shared.payload ?? {}) as {
          body_html?: string | null;
          updated_at?: string | null;
        };
        return {
          source: "shared" as const,
          title: shared.title ?? pageSlug,
          body_html: payload.body_html ?? null,
          updated_at: payload.updated_at ?? null,
        };
      }
      const pub = await loadPublicPage(tenantSlug, pageSlug);
      return { ...pub, source: "public" as const };
    },
  });
  const [voted, setVoted] = useState<boolean>(false);
  const vote = useMutation({
    mutationFn: async (helpful: boolean) => voteOnPage(tenantSlug, pageSlug, helpful),
    onSuccess: (result) => {
      setVoted(true);
      if (result.already_voted_today) {
        toast.message("You already voted today — thanks for letting us know.");
      } else {
        toast.success("Thanks for your feedback");
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
        Loading…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-center">
        <div className="max-w-md space-y-2">
          <h1 className="text-lg font-medium">Page unavailable</h1>
          <p className="text-sm text-slate-500">
            {(error as Error | null)?.message ?? "This page may have been removed."}
          </p>
          <Link
            to="/$tenantSlug/client"
            params={{ tenantSlug }}
            className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center"
          >
            <ArrowLeft className="size-4 mr-1" /> Back to portal
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 sm:p-8 bg-slate-50 dark:bg-slate-950">
      <article className="max-w-3xl mx-auto space-y-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 sm:p-10">
        <div className="space-y-2">
          {data.source === "public" && data.category_name ? (
            <span className="text-xs text-slate-500">{data.category_name}</span>
          ) : null}
          <h1 className="text-2xl sm:text-3xl font-medium">{data.title}</h1>
          {data.source === "public" && data.published_at ? (
            <p className="text-xs text-slate-400">
              Published {new Date(data.published_at).toLocaleDateString()}
            </p>
          ) : null}
        </div>
        {data.body_html ? (
          <div
            className="prose prose-slate dark:prose-invert max-w-none text-[15px] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: data.body_html }}
          />
        ) : (
          <p className="text-sm text-slate-500 italic">This page has no content yet.</p>
        )}

        <footer className="border-t border-slate-200 dark:border-slate-800 pt-6 space-y-3">
          {data.source === "public" ? (
            <>
              {/*
                * The prompt names what the reader is looking at, taken
                * from the page's own category. A support article asks
                * about a support article and a research paper asks
                * about research, with no second place to configure it —
                * renaming the category renames the prompt. A page filed
                * under nothing falls back to naming the page itself.
                */}
              <div className="text-sm font-medium">
                Rate this {data.category_name?.trim() || "page"}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => vote.mutate(true)}
                  disabled={voted || vote.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-sm disabled:opacity-60"
                >
                  <ThumbsUp className="size-4" /> Yes ({data.helpful_count})
                </button>
                <button
                  type="button"
                  onClick={() => vote.mutate(false)}
                  disabled={voted || vote.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-sm disabled:opacity-60"
                >
                  <ThumbsDown className="size-4" /> No ({data.unhelpful_count})
                </button>
              </div>
            </>
          ) : null}
          <Link
            to="/$tenantSlug/client"
            params={{ tenantSlug }}
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 pt-2"
          >
            <ArrowLeft className="size-4 mr-1" /> Back to portal
          </Link>
        </footer>
      </article>
      <p className="text-xs text-slate-400 text-center pt-4">Powered by ActionaryCRM</p>
    </div>
  );
}
